import React, { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaAlignLeft,
  FaCalendar,
  FaClipboard,
  FaComments,
  FaPaperclip,
  FaTag,
  FaUser,
} from 'react-icons/fa'
import { MdClose } from 'react-icons/md'
import { useAttachments, useComments } from '../../hooks'
import { useCard } from '../../hooks/useCards'
import { formatDate as formatDateLocale } from '../../utils/formatDate'
import { AttachmentList, CardChangeLogModal, RichTextViewer } from '../card'
import { Avatar, Button, LoadingSpinner, Modal } from '../common'

// Lazy load heavy modal
const AttachLabelModal = lazy(() =>
  import('../label').then((module) => ({ default: module.AttachLabelModal }))
)

interface CardModalProps {
  cardId: number
  boardId: number
  onClose: () => void
}

export const CalendarCardModal: React.FC<CardModalProps> = React.memo(
  ({ cardId, boardId, onClose }) => {
    const { t } = useTranslation()
    const { data: card, isLoading } = useCard(cardId)
    const { data: comments } = useComments(cardId)
    const { data: attachments = [] } = useAttachments(cardId)

    const [showLabelModal, setShowLabelModal] = React.useState(false)
    const [showChangeLogModal, setShowChangeLogModal] = React.useState(false)

    const formatDate = React.useCallback(
      (dateString?: string) => {
        if (!dateString) return t('card.noDueDate')
        return formatDateLocale(dateString, 'MMM d, yyyy')
      },
      [t]
    )

    if (isLoading || !card) {
      return (
        <Modal isOpen={true} onClose={onClose} title={t('common.loading')}>
          <div className="flex justify-center items-center py-10">
            <LoadingSpinner />
          </div>
        </Modal>
      )
    }

    return (
      <>
        <Modal isOpen={true} onClose={onClose} title="" size="lg">
          <div className="">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
              onClick={onClose}
            >
              <MdClose />
            </button>

            {/* Title Section */}
            <div className="flex items-center mb-4">
              <FaClipboard className="text-gray-500 mr-2" />
              <div>
                <h2 className="text-xl font-semibold">{card.title}</h2>
                <p className="text-gray-400 text-sm">
                  {t('card.inColumn')}{' '}
                  <span className="font-medium">{/* Column name */}</span>
                </p>
              </div>
            </div>

            <div className="border-t my-4"></div>

            {/* Main Content */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Column */}
              <div className="flex-1 space-y-6">
                {/* Members */}
                <Section title={t('card.members')} icon={<FaUser />}>
                  {card.members.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {card.members.map((member) => (
                        <div
                          key={member.userId}
                          className="flex items-center gap-2 bg-gray-100 px-2 py-1 rounded"
                        >
                          <Avatar
                            user={{ name: member.name, email: '' }}
                            size="sm"
                          />
                          <span>{member.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">
                      Ushbu kartaga a’zolar tayinlanmagan
                    </p>
                  )}
                </Section>

                {/* Labels */}
                <Section title={t('card.labels')} icon={<FaTag />}>
                  {card.labels && card.labels.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {card.labels.map((label) => (
                        <span
                          key={label.id}
                          className="px-2 py-1 rounded text-white"
                          style={{ backgroundColor: label.color }}
                        >
                          {label.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">{t('card.noLabels')}</p>
                  )}
                </Section>

                {/* Due Date */}
                <Section title={t('card.dueDate')} icon={<FaCalendar />}>
                  {card.dueDate ? (
                    <p>{formatDate(card.dueDate)}</p>
                  ) : (
                    <p className="text-gray-400">{t('card.noDueDate')}</p>
                  )}
                </Section>

                {/* Description */}
                <Section title={t('card.description')} icon={<FaAlignLeft />}>
                  {card.description ? (
                    <RichTextViewer content={card.description} />
                  ) : (
                    <p className="text-gray-400">Tavsif yo'q</p>
                  )}
                </Section>

                {/* Comments */}
                <Section title={t('card.comments')} icon={<FaComments />}>
                  {comments && comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((c) => (
                        <div key={c.id} className="border rounded p-2">
                          <div className="flex justify-between text-sm font-medium">
                            <span>{c.authorName}</span>
                            <span className="text-gray-400">
                              {new Date(c.createdAt).toLocaleString('uz-UZ')}
                            </span>
                          </div>
                          <p>{c.text}</p>
                          {c.replies?.length > 0 && (
                            <div className="ml-4 mt-2 space-y-1">
                              {c.replies.map((r) => (
                                <div key={r.id} className="text-sm">
                                  <strong>{r.authorName}</strong>: {r.text}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">{t('card.noComments')}</p>
                  )}
                </Section>

                {/* Attachments */}
                <Section title={t('card.attachments')} icon={<FaPaperclip />}>
                  <AttachmentList cardId={cardId} />
                </Section>
              </div>

              {/* Right Column - Actions */}
              <div className="w-full md:w-64 flex-shrink-0 space-y-4">
                <h3 className="font-semibold text-gray-700">
                  {t('common.actions')}
                </h3>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowChangeLogModal(true)}
                >
                  {t('card.activityLog', 'Faoliyat tarixi')}
                </Button>
              </div>
            </div>
          </div>
        </Modal>

        {showLabelModal && (
          <Suspense fallback={<LoadingSpinner />}>
            <AttachLabelModal
              isOpen={showLabelModal}
              onClose={() => setShowLabelModal(false)}
              cardId={cardId}
              boardId={boardId}
              attachedLabels={card.labels || []}
            />
          </Suspense>
        )}

        {showChangeLogModal && (
          <CardChangeLogModal
            cardId={cardId}
            cardTitle={card.title}
            onClose={() => setShowChangeLogModal(false)}
          />
        )}
      </>
    )
  }
)

interface SectionProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ title, icon, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="font-medium text-gray-700">{title}</h3>
    </div>
    <div>{children}</div>
    <div className="border-t"></div>
  </div>
)
